import { ReactElement, ReactNode } from 'react';
import { TabsProps } from 'antd';
import { useTranslation } from 'react-i18next';
import { Outlet, matchPath, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Tabs } from 'common/components/antd/Tabs/Tabs';
import { ROUTES_MAPPING } from 'navigation/constants/route.constants';
import {
  STitle,
  SContent,
  SContainer,
  STabs,
} from './reconciliation.layout.style';

type TTab = {
  path: string;
  key: string;
  label: ReactNode;
  extraContent: ReactNode;
};

const ESectionKey = {
  Statistic: 'statistic',
  ReconciliationTable: 'reconciliation-table',
  SetupNotifications: 'setup-notifications',
};

const ReconciliationLayoutRoute = (): ReactElement => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { counterparty } = useParams();

  const tabs: TTab[] = [
    {
      key: ESectionKey.Statistic,
      label: t('reconciliation.tabs.statistic.title'),
      path: ROUTES_MAPPING.PROTECTED.RECONCILIATION_STATISTIC,
      extraContent: null,
    },
    {
      key: ESectionKey.ReconciliationTable,
      label: t('reconciliation.tabs.table.title'),
      path: ROUTES_MAPPING.PROTECTED.RECONCILIATION_TABLE,
      extraContent: null,
    },
    {
      key: ESectionKey.SetupNotifications,
      label: t('reconciliation.tabs.notifications.title'),
      path: ROUTES_MAPPING.PROTECTED.RECONCILIATION_NOTIFICATIONS,
      extraContent: null,
    },
  ];

  const activeTab = tabs.find((tab) => {
    const match = matchPath(
      {
        path: tab.path,
        end: false,
        caseSensitive: false,
      },
      location.pathname,
    );

    return !!match;
  });

  const extraContent = {
    right: activeTab?.extraContent,
    left: <div style={{ width: 24 }} />,
  };

  const onChangeTab: TabsProps['onChange'] = async (key) => {
    const tabChangeTo = tabs.find((tab) => tab.key === key);
    if (tabChangeTo) {
      navigate(tabChangeTo.path.replace(':counterparty', counterparty || ''));
    }
  };

  return (
    <SContainer>
      <STitle>{t('reconciliation.title')}</STitle>
      <SContent>
        <STabs>
          <Tabs
            onChange={onChangeTab}
            activeKey={activeTab?.key}
            tabBarExtraContent={extraContent}
            defaultActiveKey="1"
            items={tabs}
          />
          <Outlet />
        </STabs>
      </SContent>
    </SContainer>
  );
};

export default ReconciliationLayoutRoute;
